import { AlertCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/services/api";
import type { Programme } from "@/types";
import { formatCurrency } from "@/utils/payment";

// Set when a guest picks a free cohort: joining requires an account, so we
// stash the cohort id, send them to /sign-up, and PendingJoinRedirect (App.tsx)
// forwards them to /register?programme_id=... afterwards to auto-join.
export const PENDING_JOIN_KEY = "pending_join_programme_id";

/**
 * Public guest checkout: pick a cohort, enter an email, and pay — no account
 * required. After payment, the buyer receives a single-use code by email to
 * redeem (see RedeemPage). Paid-only: freemium tasters live at /start
 * (KC-504), so they're left out of the list and a freemium link is forwarded
 * there. Other £0 cohorts still route through sign-up instead of Stripe.
 * Signed-in users use ProgramRegistrationPage instead.
 */
export default function GuestCheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preselectedId = searchParams.get("programme_id");
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string>("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Set only when the direct link points at a private (invite-only) cohort —
  // it's resolved by id, not from the public list, and shown alone rather
  // than mixed into the general dropdown (KC-389).
  const [privateProgramme, setPrivateProgramme] = useState<Programme | null>(null);
  // True when a programme_id was supplied but doesn't resolve to a buyable
  // cohort. We surface an explicit "not available" message and leave the
  // selection empty rather than silently substituting another cohort (KC-398).
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const all = await api.getAvailableProgrammes();
        if (all.some((p) => p.id === preselectedId && p.is_freemium)) {
          navigate(`/start?programme_id=${encodeURIComponent(preselectedId ?? "")}`, {
            replace: true,
          });
          return;
        }
        const open = all.filter((p) => p.seats_remaining !== 0 && !p.is_freemium);
        // Always render the public list so the buyer can choose deliberately,
        // even when a supplied link fails to resolve.
        setProgrammes(open);

        if (!preselectedId) {
          // No link: default to the first open cohort, as before.
          if (open.length > 0) setSelectedId(open[0].id);
          return;
        }

        const preselected = open.find((p) => p.id === preselectedId);
        if (preselected) {
          setSelectedId(preselected.id);
          return;
        }

        // Not in the public list — it may be a private (invite-only) cohort
        // reachable only by link. Resolve it by id.
        const direct = await api.getProgrammeById(preselectedId);
        if (direct?.is_freemium) {
          navigate(`/start?programme_id=${encodeURIComponent(preselectedId)}`, { replace: true });
          return;
        }
        if (direct && direct.seats_remaining !== 0) {
          setPrivateProgramme(direct);
          setSelectedId(direct.id);
          return;
        }

        // The supplied id doesn't resolve to a buyable cohort (stale, mistyped,
        // draft, archived, or full). Never substitute another cohort — say so
        // and leave the selection empty (KC-398).
        setUnavailable(true);
      } catch {
        setError("Couldn't load programmes. Please try again later.");
      } finally {
        setLoading(false);
      }
    })();
  }, [preselectedId, navigate]);

  const selected = privateProgramme ?? programmes.find((p) => p.id === selectedId);
  // Free cohorts can't go through Stripe (the API rejects price <= 0) — they
  // require an account, joined via /register after sign-up.
  const isFree = selected != null && selected.price === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedId) return;
    if (isFree) {
      sessionStorage.setItem(PENDING_JOIN_KEY, selectedId);
      navigate("/sign-up");
      return;
    }
    if (!email) return;
    setSubmitting(true);
    setError("");
    try {
      const { session_url } = await api.createGuestCheckoutSession(selectedId, email);
      window.location.href = session_url;
    } catch (err) {
      setSubmitting(false);
      setError(err instanceof Error ? err.message : "Couldn't start checkout. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-sidebar flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur-sm p-12 rounded-2xl shadow-xl max-w-md w-full border border-white/10">
        <div className="flex flex-col items-center text-center mb-8">
          <img src="/dfr-logo.svg" alt="DFR Logo" className="h-12 w-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-1">Join the Dominion Programme</h1>
          <p className="text-white/60">Choose your cohort and secure your seat.</p>
        </div>

        {loading && <Loader2 className="w-6 h-6 animate-spin text-white mx-auto" />}

        {!loading && unavailable && (
          <p className="text-amber-300 text-sm flex items-center gap-2 mb-4">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            This cohort isn't available.
            {programmes.length > 0 ? " Choose another below." : ""}
          </p>
        )}

        {!loading && !privateProgramme && programmes.length === 0 && (
          <p className="text-white/60 text-center">No cohorts are open for enrollment right now.</p>
        )}

        {!loading && (privateProgramme || programmes.length > 0) && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-white/70 text-sm mb-2">Cohort</label>
              {privateProgramme ? (
                <div className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white">
                  <div className="flex items-center gap-2">
                    <span>{privateProgramme.name}</span>
                    <span className="text-[10px] uppercase tracking-wider text-amber-300 border border-amber-300/40 rounded-full px-2 py-0.5">
                      Private
                    </span>
                  </div>
                  <p className="text-white/50 text-xs mt-1">
                    By invitation only — this cohort was shared with you directly and isn't listed
                    publicly.
                  </p>
                </div>
              ) : (
                <select
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white focus:outline-none focus:border-white/40"
                >
                  <option value="" disabled className="text-black">
                    Choose a cohort…
                  </option>
                  {programmes.map((p) => (
                    <option key={p.id} value={p.id} className="text-black">
                      {p.name} — starts {new Date(p.start_date).toLocaleDateString()}
                      {p.price === 0 ? " — FREE" : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {!isFree && (
              <div>
                <label className="block text-white/70 text-sm mb-2">Email</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-white/40"
                />
                <p className="text-white/40 text-xs mt-2">
                  We'll email your enrollment code here after payment.
                </p>
              </div>
            )}

            {isFree && (
              <p className="text-white/40 text-xs">
                This cohort is free — you'll create an account to join it.
              </p>
            )}

            {error && (
              <p className="text-red-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || !selectedId || (!isFree && !email)}
              className="w-full bg-white text-sidebar px-6 py-4 rounded-xl font-bold hover:bg-white/90 disabled:opacity-70 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {selected
                ? isFree
                  ? "Join Free"
                  : `Pay ${formatCurrency(selected.price)}`
                : "Continue"}
            </button>
          </form>
        )}

        <div className="mt-8 pt-6 border-t border-white/10 text-center">
          <p className="text-white/60 text-sm mb-2">Already have an account?</p>
          <Link
            to="/sign-in"
            className="inline-block w-full bg-transparent border border-white/30 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition-colors"
          >
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
